import React from 'react';

import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import Fade from '@smart-tv/components/fade/fade';
import testIds from '@smart-tv/e2e/shared/variables';
import { LiveTvChannel } from '@smart-tv/typings/content';
import { useAtomValue } from 'jotai';
import { isErrorDialogOpenAtom } from '@smart-tv/atoms';
import { playerWaitingAtom } from '../atoms';

import PlayerInformation from '../player-information';
import usePlayerControls from './_hooks/use-player-controls';
import { PlayerControlsNavigation } from './player-controls-navigation/player-controls-navigation';
import PlayerControlsScrubber from './player-controls-scrubber/player-controls-scrubber';
import PlayerSettingsOverlay from './player-settings-overlay';
import styles from './player-controls.module.scss';

export const NO_SUBTITLE_TRACK_ID = '-1';
export const NO_SUBTITLE_TRACK_LANG = 'none';

export interface PlayerControlsProps {
  channel?: LiveTvChannel;
  onClose?: () => void;
  open?: boolean;
  startTime?: Date;
  endTime?: Date;
}

export const PlayerControls: React.FC<PlayerControlsProps> = (props) => {
  const { open = false, channel, startTime, endTime } = props;
  const errorDialogOpen = useAtomValue(isErrorDialogOpenAtom);
  const playerWaiting = useAtomValue(playerWaitingAtom);

  const {
    focusKey,
    handleKeyDown,
    handleKeyUp,
    handleMouseOver,
    handleMouseLeave,
    handleResetControls,
    settingsOverlay,
    toggleSettingsOverlay,
    subtitleTracks,
    selectedSubtitleTrack,
    handleSelectSubtitleTrack,
    audioTracks,
    selectedAudioTrack,
    setSelectedAudioTrack,
    ref,
    player
  } = usePlayerControls(props);

  //this handles the edge case where we get an error while the player controls are open, and we want to close the controls and focus back on the player
  React.useEffect(() => {
    if (settingsOverlay) {
      toggleSettingsOverlay();
    }
  }, [errorDialogOpen]);

  return (
    <FocusContext.Provider value={focusKey}>
      <Fade in={open}>
        <div
          ref={ref}
          tabIndex={1}
          onKeyDown={!settingsOverlay ? handleKeyDown : undefined}
          onKeyUp={handleKeyUp}
          data-testid={testIds.player.controls.root}
          className={styles['player-controls']}
        >
          <div
            className={styles['player-controls__content']}
            onClick={(e) => e.stopPropagation()}
            onMouseOver={!settingsOverlay ? handleMouseOver : undefined}
            onMouseLeave={handleMouseLeave}
          >
            {!settingsOverlay ? (
              <>
                {!playerWaiting && <PlayerInformation channel={channel} />}
                <PlayerControlsScrubber endTime={endTime} startTime={startTime} />
                <PlayerControlsNavigation
                  channel={channel}
                  controlsOpen={open}
                  closeControls={handleResetControls}
                />
              </>
            ) : (
              <PlayerSettingsOverlay
                open={settingsOverlay}
                onClose={toggleSettingsOverlay}
                subtitleTracks={subtitleTracks}
                selectedSubtitleTrack={selectedSubtitleTrack}
                setSelectedSubtitleTrack={handleSelectSubtitleTrack}
                audioTracks={audioTracks}
                selectedAudioTrack={selectedAudioTrack}
                setSelectedAudioTrack={setSelectedAudioTrack}
                player={player}
              />
            )}
          </div>
        </div>
      </Fade>
    </FocusContext.Provider>
  );
};

PlayerControls.displayName = 'PlayerControls';

export default PlayerControls;
